import React, { useCallback } from 'react'
import { Field, FormikProps, useFormikContext } from 'formik';
import classnames from 'classnames';
import AnimateHeight from 'react-animate-height';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import { TextInput } from 'ui/TextInput';
import { theme } from '../../../../../tailwind.config';
import { ArrowDownComponent } from 'ui/Icons';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { useCrmTypeValues } from 'services/CrmApi/queries/useCrmTypeValues';
import { ETypeValueType } from 'services/CrmApi/types/types';
import { IContactCreateFormValues } from 'containers/CRM/newContact/contactCreateFormValidation';

interface IAddCompanyNewProps {
  isOpen: boolean;
  toggleOpen: (isOpen: boolean) => void;
}

export const AddCompanyNew: React.FC<IAddCompanyNewProps> = ({ isOpen, toggleOpen }) => {
  const form: FormikProps<IContactCreateFormValues> = useFormikContext();
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();

  const sectionClassname = classnames(
    'section-title font-pt-sans text-[17px] leading-normal text-black m-0 select-none py-0 px-0',
    { 'font-[400]': !isOpen, 'font-[700]': isOpen }
  );
  const firstLineClassname = classnames(
    'type-title-firstname-lastname flex gap-[20px]', {
      'flex-col': isMobile,
      'flex-row': isTablet || isDesktop || isDesktopHD,
    }
  );

  const crmTypeValuesCompanies = useCrmTypeValues(ETypeValueType.COMPANY, { enabled: true });
  const typeOptions: ISingleSelectOption[] = crmTypeValuesCompanies.data?.data?.map(
    item => ({ value: item.code, label: item.value })
  ) ?? [];

  const toggleSection = useCallback(() => {
    toggleOpen(!isOpen);
  }, [toggleOpen, isOpen]);

  return (
    <div className="px-[10px]">
      <div className="page-section-title font-pt-sans cursor-pointer flex justify-start items-center gap-[5px]" onClick={toggleSection}>
        <span className={classnames('flex justify-center items-center transform-transition transition-ease-in-out duration-300 cursor-pointer',
            { '-rotate-90': !isOpen, 'rotate-0': isOpen }
          )}
        >
          <ArrowDownComponent icon={theme.colors['black']} />
        </span>
        <p className={sectionClassname}>
          Create New
        </p>
      </div>
      <AnimateHeight duration={300} height={isOpen ? 'auto' : 0} className="accordion-area">
        <>
          <div className="form-container flex flex-col justify-between gap-[20px] mt-[20px] ">
            <div className={firstLineClassname}>
              <div className={classnames('type-name flex gap-[20px]', { 'flex-1': isTablet || isDesktop || isDesktopHD })}>
                <Field name="companyType">
                  {({ field: { name, value }, form: { setFieldValue } }) => (
                    <SingleSelect
                      fieldId="companyType"
                      label="Type *"
                      className={classnames('type flex-1')}
                      value={value}
                      showSelectedOption
                      maxVisibleItems={isMobile ? 4 : 6}
                      options={typeOptions ?? []}
                      labelWhenNothingSelected="None"
                      onChange={value => {
                        setFieldValue(name, value);
                      }}
                      errorMessage={form.touched.companyType && form.errors.companyType ? form.errors.companyType : null}
                      errorClassName="companyType-error"
                    />
                  )}
                </Field>
                <Field
                  as={TextInput}
                  type="text"
                  name="companyName"
                  id="companyName"
                  className={classnames('first-name flex-1')}
                  label="Name *"
                  errorMessage={form.touched.companyName && form.errors.companyName ? form.errors.companyName : null}
                  errorClassName="companyName-error"
                />
              </div>
            </div>
          </div>
        </>
      </AnimateHeight>
    </div>
  )
}